// useDraggableResizable — reusable "floating window" behavior for any card that
// floats over the viewer's `relative` container: drag it by its header, resize
// it from a bottom-right corner grip, and clamp it inside the viewer bounds.
//
// Generalized from the Render-Engine panel's bespoke `startPanelDrag`/`panelPos`
// drag in ThreeScene.tsx (same feel: pointer-relative grab, window-level
// pointermove/pointerup, live clamp to the offsetParent's rect). Adds resize on
// top of that: a corner grip that grows/shrinks the card, never below its
// default size (default size IS the minimum unless a smaller `minSize` is
// given), and never past the viewer edges.
//
// The consumer owns the container element. It must be `absolute`-positioned and
// its `offsetParent` must be the (position:relative) viewer container that the
// window should stay inside — exactly how the Render-Engine panel and the JAWS
// popup are mounted. Spread `style` onto that container, wire
// `onHeaderPointerDown` onto the drag handle (the header), and render
// `resizeHandle` as the last child of the container.
//
// Note on defaults: while the window has never been dragged or resized, `pos`
// is `null` and `style` carries only the size — so the consumer keeps its own
// Tailwind/CSS anchor (e.g. `left-1 top-1`, or a mirrored `right`). The first
// drag pins an explicit `{left, top}` (resolved from the current on-screen rect)
// and `style` takes over positioning from there. Passing `defaultPos` opts into
// an explicit initial position instead.

import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';

export interface XY {
  x: number;
  y: number;
}

export interface Size {
  w: number;
  h: number;
}

export interface UseDraggableResizableOptions {
  /**
   * Explicit initial position (container-relative px, left/top). Omit to keep
   * the consumer's own CSS anchor until the first drag — `pos` stays `null` and
   * `style` omits left/top so a Tailwind `left-1 top-1` (or a mirrored `right`)
   * still applies. The first drag resolves and pins a real position.
   */
  defaultPos?: XY;
  /** Default (and, unless `minSize` overrides, minimum) window size in px. */
  defaultSize: Size;
  /**
   * Smallest the window may be resized to. Defaults to `defaultSize` — i.e. the
   * window can never be dragged SMALLER than its default. Provide this only to
   * allow shrinking below the default.
   */
  minSize?: Size;
}

export interface ResizeHandleProps {
  onPointerDown: (e: React.PointerEvent<HTMLElement>) => void;
  className: string;
  style: CSSProperties;
  'aria-hidden': true;
}

export interface UseDraggableResizable {
  /**
   * Current explicit position (container-relative left/top), or `null` while
   * the window still sits at its CSS-anchored default (never dragged).
   */
  pos: XY | null;
  /** Current window size in px. */
  size: Size;
  /**
   * Style for the floating container: the current width/height, plus left/top
   * once the window has an explicit `pos`. Spread onto the `absolute` container.
   */
  style: CSSProperties;
  /** Start a drag. Wire onto the header's `onPointerDown`. */
  onHeaderPointerDown: (e: React.PointerEvent<HTMLElement>) => void;
  /**
   * Ready-to-render bottom-right resize grip. Render it as a child of the
   * container: `<div {...resizeHandle} />`. It's absolutely positioned in the
   * corner and starts a resize on pointer-down.
   */
  resizeHandle: ResizeHandleProps;
  /** Reset to the default position (CSS-anchored) and size. */
  reset: () => void;
}

/**
 * Draggable + resizable floating-window behavior, clamped inside the viewer.
 *
 * @example
 * const { style, onHeaderPointerDown, resizeHandle } = useDraggableResizable({
 *   defaultSize: { w: 320, h: 400 },
 * });
 * return (
 *   <div className="absolute left-1 top-1" style={style}>
 *     <header onPointerDown={onHeaderPointerDown}>…</header>
 *     <div className="overflow-auto">…</div>
 *     <div {...resizeHandle} />
 *   </div>
 * );
 */
export function useDraggableResizable({
  defaultPos,
  defaultSize,
  minSize,
}: UseDraggableResizableOptions): UseDraggableResizable {
  const min = minSize ?? defaultSize;

  const [pos, setPos] = useState<XY | null>(defaultPos ?? null);
  const [size, setSize] = useState<Size>(defaultSize);

  // Latest min/size read inside the pointer handlers without re-subscribing.
  const minRef = useRef(min);
  minRef.current = min;

  const onHeaderPointerDown = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      // Only the primary button drags; ignore clicks on interactive header
      // children that stopPropagation (buttons, the collapse toggle).
      if (e.button !== 0) return;
      // The draggable container is the nearest `data-floating-window` ancestor
      // (else the handle's positioned ancestor).
      const wrap = findFloatingContainer(e.currentTarget);
      const container = wrap?.offsetParent as HTMLElement | null;
      if (!wrap || !container) return;

      const wrapRect = wrap.getBoundingClientRect();
      const grabX = e.clientX - wrapRect.left;
      const grabY = e.clientY - wrapRect.top;

      const onMove = (ev: PointerEvent) => {
        const crect = container.getBoundingClientRect();
        const x = clamp(
          ev.clientX - crect.left - grabX,
          0,
          Math.max(0, crect.width - wrapRect.width),
        );
        const y = clamp(
          ev.clientY - crect.top - grabY,
          0,
          Math.max(0, crect.height - wrapRect.height),
        );
        setPos({ x, y });
      };
      const onUp = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      // Suppress the accidental text-selection that a header drag would start.
      e.preventDefault();
    },
    [],
  );

  const onResizePointerDown = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      if (e.button !== 0) return;
      const wrap = findFloatingContainer(e.currentTarget);
      const container = wrap?.offsetParent as HTMLElement | null;
      if (!wrap || !container) return;
      // Resize is independent of drag — don't let the pointerdown bubble to a
      // header/container drag handler.
      e.stopPropagation();
      e.preventDefault();

      const wrapRect = wrap.getBoundingClientRect();
      const startW = wrapRect.width;
      const startH = wrapRect.height;
      const startX = e.clientX;
      const startY = e.clientY;
      // Container-relative top-left of the window at resize start — the max size
      // is bounded by the distance from here to the container's far edges.
      const crectStart = container.getBoundingClientRect();
      const originLeft = wrapRect.left - crectStart.left;
      const originTop = wrapRect.top - crectStart.top;

      const onMove = (ev: PointerEvent) => {
        const crect = container.getBoundingClientRect();
        const maxW = Math.max(minRef.current.w, crect.width - originLeft);
        const maxH = Math.max(minRef.current.h, crect.height - originTop);
        const w = clamp(startW + (ev.clientX - startX), minRef.current.w, maxW);
        const h = clamp(startH + (ev.clientY - startY), minRef.current.h, maxH);
        setSize({ w, h });
      };
      const onUp = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [],
  );

  const reset = useCallback(() => {
    setPos(defaultPos ?? null);
    setSize(defaultSize);
    // defaultPos/defaultSize are stable option literals from the caller; reset
    // intentionally restores the values captured at mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const style = useMemo<CSSProperties>(
    () => ({
      width: size.w,
      height: size.h,
      ...(pos ? { left: pos.x, top: pos.y } : null),
    }),
    [size.w, size.h, pos],
  );

  const resizeHandle = useMemo<ResizeHandleProps>(
    () => ({
      onPointerDown: onResizePointerDown,
      className:
        'absolute bottom-0 right-0 z-50 h-4 w-4 cursor-se-resize touch-none',
      style: {
        // A subtle corner grip: two diagonal lines drawn with a conic-ish
        // gradient so it reads as a resize affordance without extra markup.
        background:
          'linear-gradient(135deg, transparent 0 55%, currentColor 55% 60%, transparent 60% 70%, currentColor 70% 75%, transparent 75%)',
        color: 'rgba(255,255,255,0.4)',
      },
      'aria-hidden': true,
    }),
    [onResizePointerDown],
  );

  return { pos, size, style, onHeaderPointerDown, resizeHandle, reset };
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(v, hi));
}

/**
 * From a header/grip element, find the floating container to move/resize: the
 * nearest ancestor tagged `data-floating-window`, else the nearest positioned
 * ancestor (`offsetParent` of the handle). Tagging is the robust path — the
 * consumer sets `data-floating-window` on the container.
 */
function findFloatingContainer(el: HTMLElement): HTMLElement | null {
  const tagged = el.closest<HTMLElement>('[data-floating-window]');
  if (tagged) return tagged;
  // Fallback: the handle's positioned ancestor.
  return el.offsetParent as HTMLElement | null;
}
